import { inject } from '@angular/core';
import { CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { MobileDetectionService } from 'app/core/services/mobile-detection.service';

/**
 * CanMatch guard that diverts mobile devices to the mobile (`/m/*`)
 * routes BEFORE the desktop route is ever matched.
 *
 * Why a CanMatch guard instead of the old `LayoutComponent.ngOnInit`
 * redirect:
 *   The previous approach redirected from inside `ngOnInit`, which only
 *   fires AFTER the router has matched the desktop route, run its
 *   resolver, instantiated `LayoutComponent`, and begun painting the
 *   desktop layout + page. On a phone you'd briefly see the full
 *   desktop UI render and THEN jump to the mobile view — the "desktop
 *   flashes first" bug.
 *
 *   CanMatch runs during route *matching*, before any component is
 *   created or resolver runs. Returning a `UrlTree` here cancels the
 *   desktop navigation and starts a fresh one to the mobile equivalent,
 *   so the desktop layout never mounts and there's nothing to flash.
 *
 * Behaviour mirrors the old skip-list logic: only authenticated users
 * on a mobile device are redirected, and auth / public / already-mobile
 * routes are left alone.
 */
export const mobileRedirectGuard: CanMatchFn = (
    _route: Route,
    segments: UrlSegment[],
) => {
    const router = inject(Router);
    const mobileDetection = inject(MobileDetectionService);

    // Desktop devices always match desktop routes.
    if (!mobileDetection.isMobile) {
        return true;
    }

    // Build the path the user is attempting to reach from the URL
    // segments the router is testing against this route.
    const attemptedPath = '/' + segments.map((s) => s.path).join('/');

    // Routes that must stay on their desktop / empty-layout version
    // even on a mobile device (auth flows, public pages, etc.).
    const skipRedirectPaths = [
        '/m/',
        '/kiosk',
        '/sign-in',
        '/sign-up',
        '/sign-out',
        '/forgot-password',
        '/reset-password',
        '/unlock-session',
        '/confirmation-required',
        '/resource-view',
        '/home',
        '/install',
    ];
    if (skipRedirectPaths.some((path) => attemptedPath.includes(path))) {
        return true;
    }

    // Only redirect authenticated users; let everyone else flow through
    // the normal sign-in process first.
    if (typeof localStorage === 'undefined') {
        return true;
    }
    const userData = localStorage.getItem('userData');
    if (!userData || userData === 'null' || userData === 'undefined') {
        return true;
    }

    // Map known desktop sections to their closest mobile equivalent;
    // anything without a dedicated mobile screen falls back to the
    // mobile orders home.
    const firstSegment = segments[0]?.path ?? '';
    const mobileTargetBySection: Record<string, string> = {
        orders: '/m/orders',
        delivery: '/m/delivery',
        chat: '/m/chat',
        mailbox: '/m/email',
    };
    const target = mobileTargetBySection[firstSegment] ?? '/m/orders';

    console.log(
        `📱 Mobile device detected, diverting "${attemptedPath}" to "${target}" before desktop render`,
    );

    return router.parseUrl(target);
};
